import type { ICaaCustomerAddress } from "@repo/shopify/queries/customer-account-gql";
import { formatOrderAddress } from "@repo/shopify/utils/address";
import type { MailingAddress } from "@shopify/hydrogen-react/storefront-api-types";
import { t } from "i18next";
import React from "react";

import Button from "@atoms/Button";

import Address from "@molecules/Address";

import useLanguage from "@hooks/useLanguage";

import { useLayoutStore } from "@store/layout";

interface IProps {
  address: MailingAddress | ICaaCustomerAddress;
  isJp?: boolean;
  isDefaultAddress?: boolean;
  isDeleteLoading?: boolean;
  onClickEdit?: () => void;
  onDelete?: () => Promise<void>;
}

// jp(CAA)는 Shopify가 내려주는 formatted 라인을 그대로 쓴다. classic/intl은 formatOrderAddress로 재구성한 Address 컴포넌트를 쓴다.
function CaaAddressDisplay({ address }: { address: ICaaCustomerAddress }) {
  return (
    <div className="flex flex-col">
      <span>{address.name || `${address.lastName || ""} ${address.firstName || ""}`}</span>
      {(address.formatted ?? []).map((line) => (
        <span key={line}>{line}</span>
      ))}
      {address.phoneNumber ? <span>{address.phoneNumber}</span> : null}
    </div>
  );
}

export default function AddressItem({
  address,
  isJp = false,
  isDefaultAddress = false,
  isDeleteLoading = false,
  onClickEdit,
  onDelete,
}: IProps) {
  const { openModal } = useLayoutStore();
  const { lang } = useLanguage();

  const handleDeleteAddress = () => {
    openModal({
      title: "Delete Address",
      content: <p>{t("addresses.delete_address")}</p>,
      confirm: {
        label: "Delete",
        onClick: () => {
          void (onDelete?.() ?? Promise.resolve()).then(() => {
            openModal(null);
          });
        },
      },
      cancel: {
        label: "Cancel",
        onClick: () => {
          openModal(null);
        },
      },
    });
  };

  const addressDisplay = isJp ? (
    <CaaAddressDisplay address={address as ICaaCustomerAddress} />
  ) : (
    <Address {...formatOrderAddress({ address: address as MailingAddress, lang })} />
  );

  return (
    <div className="flex flex-col gap-2.5">
      {isDefaultAddress ? (
        <span className="text-blue uppercase">Default</span>
      ) : (
        <span className="pc:invisible pc:block hidden">Address</span>
      )}
      {addressDisplay}
      <div className="flex gap-2.5">
        <Button as="text" disabled={isDeleteLoading} onClick={onClickEdit}>
          Edit
        </Button>
        <Button as="text" disabled={isDeleteLoading} onClick={handleDeleteAddress}>
          Delete
        </Button>
      </div>
    </div>
  );
}
